/**
 * Boot-time session bootstrap for StudentPortal (mirror of Sandbox's
 * `Core/utils/bootstrapSessionFromCookie.js`).
 *
 * Makes auto-login symmetric: just as the standalone Sandbox rebuilds its
 * session from the shared cy-sso cookies on boot, a **fresh Portal tab** does
 * the same. So logging in on Sandbox → opening Portal lands the user logged-in
 * without a second login.
 *
 * Source-of-truth rule: the shared `Authorization` / `refresh-token` cookies on
 * `.codeyoung.com` are the arbiter. If a local session already exists but its
 * token differs from the cookie (e.g. a profile switch happened in another tab
 * and bumped `cy_auth_sync`), the cookie token wins.
 *
 * Runs synchronously before ReactDOM.render and AFTER consumeAuthBridge() —
 * the `_cy_auth_bridge` redirect bridge stays the primary explicit hand-off;
 * this is the fresh-tab / cross-tab fallback.
 */

import { jwtDecode } from "jwt-decode";
import { decrypt } from "./other";
import { getCookie, deleteCookieAllDomains, isLoggedOut } from "./cookieUtils";
import { saveSession } from "./saveSession";
import { isNewerToken, tokenRole } from "../services/tokenManager";

// A token is "cy-sso" once it carries accessTokenSecretId (added post-migration).
const isCySsoToken = (token) => {
  try {
    return !!jwtDecode(token)?.accessTokenSecretId;
  } catch {
    return false;
  }
};

// The user/student id a token belongs to (used to tell a profile SWITCH —
// different user — apart from a same-user token migration).
const tokenUserId = (token) => {
  try {
    const d = jwtDecode(token) || {};
    return d.userId || d.studentId || d.sub || null;
  } catch {
    return null;
  }
};

export const bootstrapSessionFromCookie = () => {
  // A cross-app logout signal outranks any lingering auth cookie — never
  // auto-login a session the user just logged out of. (A fresh login clears the
  // flag via saveSession, so this only blocks stale post-logout state.)
  if (isLoggedOut()) return false;

  const accessToken = getCookie("Authorization");
  const refreshToken = getCookie("refresh-token");
  if (!accessToken || !refreshToken) return false;

  // Only STUDENT sessions participate in cross-app auto-login (symmetric with
  // the student-only cookie write in saveSession). A non-student cookie is
  // one-shot handoff transport (e.g. Epicenter one-click), not a sync source —
  // staff refreshes never re-write the shared cookies, so a lingering copy is
  // stale by definition and must never seed or overwrite a session here.
  if (tokenRole(accessToken) !== "student") return false;

  let current = {};
  try {
    current = decrypt(localStorage.getItem("user")) || {};
  } catch {
    current = {};
  }

  const localToken = current.ACCESS_TOKEN || current.Authorization;
  const stored = !!localStorage.getItem("user");

  // Adopt the shared cookie token when there's no local session (fresh-tab
  // auto-login) OR a local session exists but the cookie carries a DIFFERENT
  // token (a login / profile-switch happened in the other app / another tab).
  // The shared cookies are the source of truth. We deliberately do NOT gate
  // this on a sync flag: on localhost the cookies are shared across ports, so
  // whichever app boots first would consume the flag and the other app would
  // then never adopt — token comparison is the reliable cross-app signal.
  let shouldAdopt = !stored || localToken !== accessToken;

  // Guard ONLY against same-user token-migration churn (cy-sso → legacy on the
  // same account). Never block a profile SWITCH (a different user) — that is the
  // cross-app sync we want.
  if (shouldAdopt && stored) {
    const sameUser =
      tokenUserId(localToken) &&
      tokenUserId(localToken) === tokenUserId(accessToken);
    if (sameUser && isCySsoToken(localToken) && !isCySsoToken(accessToken)) {
      shouldAdopt = false;
    }
    // Freshness guard: over an EXISTING session, adopt only a cookie token
    // issued STRICTLY LATER than the local one. Every legitimate sync source
    // (login, profile switch, refresh) mints a brand-new token, so a not-newer
    // cookie is stale — adopting it would downgrade a live session to
    // rotated-away tokens and log the user out on the next request.
    if (shouldAdopt && !isNewerToken(accessToken, localToken)) {
      shouldAdopt = false;
    }
  }

  if (!shouldAdopt) {
    deleteCookieAllDomains("cy_auth_sync");
    return false;
  }

  // saveSession decodes the JWT for role/email/userId and writes the session.
  saveSession({ accessToken, refreshToken });

  // Consume the sync flag so subsequent boots don't needlessly re-adopt.
  deleteCookieAllDomains("cy_auth_sync");
  return true;
};

export default bootstrapSessionFromCookie;
